import React from 'react';
import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import {
  resolveAnalysisFailureCopy,
  type AnalysisStatusFields,
} from '../utils/analysisStatusCopy';

export type AnalysisStage =
  | 'queued'
  | 'downloading'
  | 'download_complete'
  | 'job_starting'
  | 'video_received'
  | 'gemini_upload'
  | 'gemini_processing'
  | 'report_finalization'
  | 'completed'
  | 'failed';

export interface AnalysisStageProgressProps {
  stage?: string;
  status?: string;
  message?: string;
  progress?: number;
  errorMessage?: string;
  failedStep?: string;
  headline?: string;
  detail?: string;
  compact?: boolean;
}

const STAGE_RANK: Record<string, number> = {
  queued: 0,
  downloading: 1,
  download_complete: 2,
  job_starting: 3,
  video_received: 4,
  gemini_upload: 5,
  gemini_processing: 6,
  report_finalization: 7,
  completed: 8,
  failed: -1,
  // Legacy stage aliases during rollout
  sampling: 5,
  vision: 6,
  report_generation: 7,
};

function stageRank(stage?: string): number {
  if (!stage) return 0;
  return STAGE_RANK[stage] ?? 0;
}

type StepState = 'complete' | 'active' | 'pending';

interface ChecklistStep {
  id: string;
  label: string;
  getState: (rank: number, stage?: string) => StepState;
}

const CHECKLIST_STEPS: ChecklistStep[] = [
  {
    id: 'upload',
    label: 'Uploading to Secure Storage',
    getState: (rank, stage) => {
      if (stage === 'downloading' || stage === 'download_complete') return 'active';
      return rank >= 2 ? 'complete' : rank >= 1 ? 'active' : 'pending';
    },
  },
  {
    id: 'gemini',
    label: 'Analyzing Game Film (Powered by Gemini)',
    getState: (rank, stage) => {
      if (
        stage === 'video_received' ||
        stage === 'gemini_upload' ||
        stage === 'gemini_processing' ||
        stage === 'job_starting' ||
        stage === 'sampling' ||
        stage === 'vision'
      ) {
        return 'active';
      }
      return rank >= 7 ? 'complete' : 'pending';
    },
  },
  {
    id: 'report',
    label: 'Finalizing Scouting Report',
    getState: (rank, stage) => {
      if (stage === 'report_finalization' || stage === 'report_generation') return 'active';
      return rank >= 8 ? 'complete' : 'pending';
    },
  },
  {
    id: 'done',
    label: 'Complete',
    getState: (_rank, stage) => (stage === 'completed' ? 'complete' : 'pending'),
  },
];

function StepIcon({ state }: { state: StepState }) {
  if (state === 'complete') {
    return <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />;
  }
  if (state === 'active') {
    return <Loader2 className="h-5 w-5 text-primary-600 animate-spin flex-shrink-0" />;
  }
  return <Circle className="h-5 w-5 text-secondary-300 dark:text-secondary-600 flex-shrink-0" />;
}

const AnalysisStageProgress: React.FC<AnalysisStageProgressProps> = ({
  stage,
  status,
  message,
  progress,
  errorMessage,
  failedStep,
  headline,
  detail,
  compact = false,
}) => {
  const rank = stageRank(stage);
  const isFailed = stage === 'failed' || status === 'failed' || status === 'error';
  const isComplete = stage === 'completed' || status === 'completed';

  const statusFields: AnalysisStatusFields = {
    status,
    stage,
    progress,
    message,
    error_message: errorMessage,
    failed_step: failedStep,
  };
  const failureCopy = resolveAnalysisFailureCopy(statusFields);

  if (isFailed) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4">
        <div className="flex items-start gap-3">
          <XCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-secondary-900 dark:text-white">
              {failureCopy?.title || 'Analysis failed'}
            </p>
            <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-300">
              {failureCopy?.message || message || 'An error occurred during analysis.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-secondary-900 dark:text-white">Analysis complete</p>
            <p className="mt-1 text-sm text-secondary-600 dark:text-secondary-300">
              Your scouting report is ready.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      {(headline || detail) && !compact && (
        <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-100 dark:border-primary-800 px-4 py-3">
          {headline && (
            <p className="text-sm font-semibold text-secondary-900 dark:text-white">{headline}</p>
          )}
          {detail && (
            <p className="text-sm text-secondary-700 dark:text-secondary-300 mt-1">{detail}</p>
          )}
        </div>
      )}

      <ul className="space-y-3">
        {CHECKLIST_STEPS.map((step) => {
          const state = step.getState(rank, stage);
          return (
            <li key={step.id} className="flex items-center gap-3">
              <StepIcon state={state} />
              <span
                className={
                  state === 'active'
                    ? 'font-medium text-secondary-900 dark:text-white'
                    : state === 'complete'
                      ? 'text-secondary-700 dark:text-secondary-300'
                      : 'text-secondary-400 dark:text-secondary-500'
                }
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AnalysisStageProgress;
